
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { formatDurationSeconds, WindowType } from '../../../util';
import { Board, Card, CardDuration, KanbanTimeTrackingStartRequest, KanbanTimeTrackingStopRequest, Ticket, TimeTracker } from '../../backend/backendapi';
import { KanbanState } from '../kanban.model';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import RunningTimeTracker from './RunningTimeTracker';
import SelectTimeTracking from './SelectTimeTracking';
import { RecentlyTrackedCard } from './models';
import RecentTimeTracker from './RecentTimeTracker';
import { getCardById } from './util';
import TrackerOverrunModal from './TrackerOverrunModal';

Vue.use(Toasted, { duration: 2000 });

declare var window: WindowType;

@Component({
  name: 'TimeTracking',
  components: {
    RecentTimeTracker,
    RunningTimeTracker,
    SelectTimeTracking,
    TrackerOverrunModal,
    vSelect,
  },
})
export default class TimeTracking extends Vue {
  @Prop({ required: true })
  readonly state!: KanbanState;

  selectedBoard: Board | null = null;
  selectedFlow: Ticket | null = null;
  selectedCard: Card | null = null;

  tickerId: any;
  ticker: number = 0;
  
  trackerOverrunTriggerSeconds = 10 * 60 * 60;

  //getter
  get boards(): Board[] {
    return this.state.boards.filter(b => b.tickets?.some(t => t.cards?.some(c => c.responsibles?.some(r => r.userId === this.userId))));
  }

  get flows(): Ticket[] {
    return this.selectedBoard?.tickets?.filter(t => t.cards?.some(c => c.responsibles?.some(r => r.userId === this.userId))) ?? [];
  }

  get cards(): Card[] {
    return this.selectedFlow?.cards?.filter(c => c.responsibles?.some(r => r.userId === this.userId)) ?? [];
  }

  get lastDuration(): string {
    let durations = this.selectedCard?.durations?.filter(e => e.userId === this.userId) ?? [];
    if (!durations.length)
      return '';

    return formatDurationSeconds(durations[durations?.length - 1].seconds);
  }

  get userId(): string | undefined {
    return window.application.kanban.user?.id;
  }

  get recentlyStartedCards(): RecentlyTrackedCard[] {
    const cards: { board: Board, flow: Ticket, card: Card, duration: CardDuration }[] = [];
    this.state.boards.forEach(b => b.tickets?.forEach(t => t.cards?.forEach(c => {
      if (c.responsibles?.some(r => r.userId === this.userId)
          && c.durations?.some(e => e.userId == this.userId)) {
        const durs = c.durations?.filter(e => e.userId == this.userId);
        cards.push({ board: b, flow: t, card: c, duration: durs[durs.length - 1] });
      }
    })));

    //sort desc
    cards.sort((l, r) => r.duration.timeStmp!.getTime() - l.duration.timeStmp!.getTime());

    const runningTrackerCardIds = new Set(this.state.timeTrackers.map(e => e.cardId));
    return cards.slice(0, 7).filter(e => !runningTrackerCardIds.has(e.card.id!));
  }

  //life cycle
  mounted(): void {
    this.tickerId = setInterval(() => this.ticker++, 499);
    this.loadOverRunTriggerSecondsSetting();
  }

  unmounted(): void {
    clearInterval(this.tickerId);
  }

  //functions
  async loadOverRunTriggerSecondsSetting(): Promise<void> {
    this.trackerOverrunTriggerSeconds = (await this.state.settingsAPI.getSystemSetting("workflow.timeTracking.trackerOverrun.triggerSeconds")).result as number;
  }
  
  selectRecentCard(event: RecentlyTrackedCard): void {
    this.selectedBoard = event.board;
    this.selectedFlow = event.flow;
    this.selectedCard = event.card;
  }

  async start(comment: string): Promise<void> {
    const tracker = await this.state.timeTrackingApi.start(new KanbanTimeTrackingStartRequest({ cardId: this.selectedCard!.id!, comment: comment }));
    this.state.timeTrackers.push(tracker);
  }

  async startRecentCard(event: RecentlyTrackedCard): Promise<void> {
    this.selectRecentCard(event);
    const tracker = await this.state.timeTrackingApi.start(new KanbanTimeTrackingStartRequest({ cardId: event.card.id!, comment: event.duration.comment }));
    this.state.timeTrackers.push(tracker);
  }

  async stop(tracker: TimeTracker, comment: string): Promise<void> {
    if (!tracker) {
      return;
    }

    const card = getCardById(this.state, tracker.cardId);
    if (!card) {
      return;
    }

    const seconds = (Date.now() - tracker.startTime.getTime()) / 1000;
    if (seconds > this.trackerOverrunTriggerSeconds) {
      (this.$refs.overrunModal as TrackerOverrunModal).open(card, tracker, comment);
      return;
    }

    const duration = await this.state.timeTrackingApi.stop(new KanbanTimeTrackingStopRequest({ id: tracker.id, comment: comment }));
    card.durations ??= [];
    card.durations.push(duration);

    const index = this.state.timeTrackers.findIndex(e => e.id === tracker.id);
    if (index > -1) {
      this.state.timeTrackers.splice(index, 1);
    }
  }

  async updateComment(tracker: TimeTracker, comment: string): Promise<void> {
    if (!tracker) {
      return;
    }
    const toBackendTracker = new TimeTracker({ ...tracker, comment: comment });
    const fromBackendTracker = await this.state.timeTrackingApi.update(toBackendTracker);
    Object.assign(tracker, fromBackendTracker);
  }

  //watches
  @Watch('selectedBoard')
  private onSelectedBoardChanged(val: Board | null) {
    if (!val?.tickets?.some(t => t.id === this.selectedFlow?.id)) {
      this.selectedFlow = null;
    }
  }

  @Watch('selectedFlow')
  private onSelectedFlowChanged(val: Ticket | null) {
    if (!val?.cards?.some(c => c.id === this.selectedCard?.id)) {
      this.selectedCard = null;
    }
  }

  @Watch('state.loading', { immediate: true }) //immediate required, otherwise the function is not called on page load
  private onReloaded(loading: boolean) {
    if (loading) {
      return;
    }

    if (this.state.timeTrackers.length) {
      const tracker = this.state.timeTrackers[0];
      loop:
          for (let board of this.state.boards) {
            for (let flow of (board.tickets ?? [])) {
              for (let card of (flow.cards ?? [])) {
                if (card.id === tracker.cardId) {
                  this.selectedBoard = board;
                  this.selectedFlow = flow;
                  this.selectedCard = card;
                  break loop;
                }
              }
            }
          }
    }
  }
}
